import React, { useState, useEffect, useRef } from 'react';
import { HookHouseTemplate } from '@/types/template.types';
import { useTemplates } from '@context/TemplateContext';
import { TemplateManagerModal } from './TemplateManagerModal';
import { SaveTemplateModal } from './SaveTemplateModal';
import { X, Save, Settings, Play, Trash2, Library, Search } from 'lucide-react';
import { Button, IconButton } from '../ui/Button';
import { Input } from '../ui/Form';
import { Badge } from '../ui/Badge';
import { Card } from '../ui/Card';

interface TemplatePanelProps {
  isOpen: boolean;
  onClose: () => void;
  onLoadTemplate: (template: HookHouseTemplate) => void;
  getCurrentConfig: () => HookHouseTemplate['config'];
}

export const TemplatePanel: React.FC<TemplatePanelProps> = ({
  isOpen,
  onClose,
  onLoadTemplate,
  getCurrentConfig
}) => {
  const { templates, deleteTemplate } = useTemplates();
  const [showManager, setShowManager] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab' && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (confirmDelete === id) {
      deleteTemplate(id);
      setConfirmDelete(null);
    } else {
      setConfirmDelete(id);
      setTimeout(() => setConfirmDelete(null), 3000);
    }
  };

  const filteredTemplates = templates.filter(t =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <>
      {/* Overlay Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] animate-in fade-in duration-300"
          onClick={onClose}
        />
      )}

      {/* Flyout Menu */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Architecture Vault"
        className={`fixed left-0 top-0 h-full w-[380px] bg-slate-950 border-r border-slate-800 z-[70] shadow-2xl transition-transform duration-500 ease-out transform ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } flex flex-col`}
      >
        {/* Header */}
        <header className="p-6 border-b border-slate-800 bg-slate-900/50">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-accent rounded-lg text-slate-950">
                <Library size={18} />
              </div>
              <div>
                <h2 className="text-lg font-black text-slate-50 uppercase tracking-tighter leading-none">Architecture Vault</h2>
                <p className="text-[11px] text-slate-400 font-bold uppercase tracking-widest mt-1">Session Templates</p>
              </div>
            </div>
            <IconButton
              onClick={onClose}
              variant="ghost"
              size="sm"
              title="Close panel"
            >
              <X size={20} />
            </IconButton>
          </div>

          <div className="space-y-4">
            <Input
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="sm"
              leftElement={<Search className="text-slate-400" size={14} />}
              className="rounded-xl shadow-inner"
            />

            <div className="flex gap-2">
              <Button
                variant="primary"
                size="sm"
                fullWidth
                leftIcon={<Save size={14} />}
                onClick={() => setShowSaveModal(true)}
                className="text-[11px] font-black uppercase tracking-widest"
              >
                Save Current
              </Button>
              <IconButton
                variant="secondary"
                size="sm"
                onClick={() => setShowManager(true)}
                title="Manage Library"
              >
                <Settings size={18} />
              </IconButton>
            </div>
          </div>
        </header>

        {/* List Content */}
        <div className="flex-grow overflow-y-auto custom-scrollbar p-4 space-y-3">
          {templates.length === 0 ? (
            <div className="h-64 flex flex-col items-center justify-center text-center p-8 space-y-4">
              <div className="w-16 h-16 rounded-full bg-slate-900 flex items-center justify-center text-slate-700 border border-slate-800">
                <Library size={32} />
              </div>
              <div>
                <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">Library Empty</p>
                <p className="text-[11px] text-slate-400 mt-2 italic leading-relaxed">Save your current studio configuration as a template to recall it later.</p>
              </div>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="p-8 text-center text-[11px] text-slate-400 font-bold uppercase">No matches found for "{searchQuery}"</div>
          ) : (
            filteredTemplates.map(template => (
              <Card
                key={template.id}
                variant="interactive"
                padding="md"
                radius="xl"
                className="group relative"
                onClick={() => {
                  onLoadTemplate(template);
                  onClose();
                }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-slate-950 flex items-center justify-center text-2xl shadow-inner border border-slate-800 group-hover:border-accent/20 transition-colors">
                    {template.icon}
                  </div>
                  <div className="flex-grow min-w-0 pr-8">
                    <div className="flex items-center gap-2">
                       <h4 className="text-sm font-black text-slate-200 truncate">{template.name}</h4>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                       {template.config.musicalBlend.slice(0, 2).map(b => (
                         <Badge
                           key={b}
                           variant="default"
                           size="xs"
                           className="text-[11px] font-black uppercase tracking-tighter"
                         >
                           {b}
                         </Badge>
                       ))}
                    </div>
                    {template.description && (
                      <p className="text-[11px] text-slate-400 line-clamp-1 mt-2 italic font-medium">"{template.description}"</p>
                    )}
                  </div>
                </div>

                <div className="absolute top-4 right-4 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <IconButton
                    onClick={(e) => handleDelete(e, template.id)}
                    variant={confirmDelete === template.id ? 'danger' : 'ghost'}
                    size="xs"
                    title="Delete Template"
                    className={confirmDelete === template.id ? '' : 'bg-slate-950 border border-slate-800 hover:border-red-900/50 hover:text-red-500'}
                  >
                    <Trash2 size={12} />
                  </IconButton>
                  <div className="p-2 bg-accent rounded-lg text-slate-950 shadow-lg shadow-amber-900/20">
                    <Play size={12} fill="currentColor" />
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>

        {/* Footer */}
        <footer className="p-6 border-t border-slate-800 bg-slate-950">
           <div className="flex justify-between items-center text-[11px] font-black text-slate-400 uppercase tracking-widest">
              <span>{templates.length} Saved Profiles</span>
              <span className="text-slate-800">v4.1.51 Vault</span>
           </div>
        </footer>
      </div>

      {/* Existing Modals remain controlled within here or lifted if needed */}
      {showManager && <TemplateManagerModal onClose={() => setShowManager(false)} />}
      {showSaveModal && <SaveTemplateModal onClose={() => setShowSaveModal(false)} getCurrentConfig={getCurrentConfig} />}
    </>
  );
};
